import { useState } from 'react';
import { HighlightType, ImageType } from '../../types/profile';
import JWInput from '../brand/JWInput';
import JWModal from '../brand/JWModal';
import { SpotifyIcon, UploadIcon, YoutubeIcon, SoundcloudIconSVG } from '../icons';
import { MinusCircle } from '../icons';
import { ChevronLeft } from 'lucide-react';
import { STEP_BACK_CLASS } from '../../pages/Onboarding/OnboardingUI';
import {
    isSpotifyTrack,
    extractSpotifyTrackId,
    isYouTubeVideo,
    extractYouTubeVideoId,
    isSpotifyPlaylist,
    extractSpotifyPlaylistId,
    extractSpotifyId,
    isSoundCloudTrack,
    extractSoundCloudTrackUrl
} from '../../utils/highlightUtil';
import { getTrackDetails, getPlaylistDetails, getAlbumDetails } from '../../api/spotify';
import axios from 'axios';

interface UploadHighlightsModalProps {
    show: boolean
    setShow: (show: boolean) => void
    onClose: () => void
    editHighlightsClick: () => void
    addHighlight: (
        caption: string,
        url: string, image: ImageType | undefined,
        mediaUrl?: string,
        coverArtUrl?: string,
        typeCd?: string
    ) => void
    highlights?: HighlightType[] | null
}

function UploadHighlightsModal(props: UploadHighlightsModalProps) {
    // Content states
    const [uploadFile, setUploadFile] = useState<ImageType>();
    const [mediaUrl, setMediaUrl] = useState<string>(''); // spotify track url, youtube video url, etc.
    const [typeCd, setTypeCd] = useState<string>(''); // C = custom upload, S = spotify track, Y = youtube video, etc.
    const [spotifyTrackId, setSpotifyTrackId] = useState<string>('');
    const [youtubeVideoId, setYoutubeVideoId] = useState<string>('');

    // UI states
    const [currentStep, setCurrentStep] = useState<number>(1); // 1 = content selection, 2 = caption/details
    const [dragActive, setDragActive] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string>('');
    const [captionError, setCaptionError] = useState<string>('');
    const [caption, setCaption] = useState<string>('');
    const [url, setUrl] = useState<string>('');

    // Format URL to ensure it has https:// prefix if needed and clean up spaces
    const formatLink = (link: string): string => {
        if (!link) return '';

        // Clean up the URL by:
        // 1. Trimming whitespace
        // 2. Replacing spaces with %20
        // 3. Ensuring https:// prefix
        const cleanedLink = link.trim().replace(/\s+/g, '%20');

        if (cleanedLink.startsWith('http://') || cleanedLink.startsWith('https://')) {
            return cleanedLink;
        }
        return `https://${cleanedLink}`;
    };

    const onFileChange = (files: FileList | null) => {
        if (files && files[0]) {
            // Validate file type
            const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            if (!validTypes.includes(files[0].type)) {
                alert('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
                return;
            }

            // Validate file size (2MB = 2 * 1024 * 1024 bytes)
            const maxSize = 2 * 1024 * 1024;
            if (files[0].size > maxSize) {
                alert('File size exceeds 2MB limit. Please choose a smaller file.');
                return;
            }

            const tmpUrl = URL.createObjectURL(files[0]);
            setUploadFile({ url: tmpUrl, file: files[0] });

            const reader = new FileReader();
            reader.onload = function (e) {
                const img = document.getElementById('highlight-img-preview') as HTMLImageElement;
                if (img) {
                    img.src = e.target?.result as string;
                }
            };
            reader.readAsDataURL(files[0]);
            setTypeCd('C');
            // Move to next step
            setCurrentStep(2);
        }
    };

    const goBack = () => {
        setCurrentStep(1);
        // Reset states
        setMediaUrl('');
        setTypeCd('');
        setSpotifyTrackId('');
        setYoutubeVideoId('');
        setUploadFile(undefined);
        setCaption('');
        setUrl('');
        setErrorMsg('');
        setCaptionError('');
        const embeddedContent = document.getElementById('embeddedContent');
        if (embeddedContent) embeddedContent.innerHTML = '';
    };

    const resetForm = () => {
        setCurrentStep(1);
        setMediaUrl('');
        setTypeCd('');
        setSpotifyTrackId('');
        setYoutubeVideoId('');
        setUploadFile(undefined);
        setCaption('');
        setUrl('');
        setErrorMsg('');
        setCaptionError('');
        const embeddedContent = document.getElementById('embeddedContent');
        if (embeddedContent) embeddedContent.innerHTML = '';
    };

    const closeModal = () => {
        resetForm();
        props.onClose();
    };

    const handleDrop = function (e: any) {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files) {
            onFileChange(e.dataTransfer.files);
        }
    };

    const handleDrag = function (e: any) {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const validateForm = () => {
        // Reset any previous errors
        setCaptionError('');

        // For custom uploads, if URL is provided, caption is required
        if (typeCd === 'C' && url.trim() && !caption.trim()) {
            setCaptionError('Caption is required when adding a URL');
            return false;
        }

        return true;
    };

    const saveHighlights = async () => {
        if (errorMsg) {
            return;
        }

        // Check if maximum highlights reached
        if (props.highlights && props.highlights.length >= 30) {
            alert('Maximum limit of 30 highlights reached. Please delete some highlights before adding more.');
            return;
        }

        // Validate the form before proceeding
        if (!validateForm()) {
            return;
        }

        let coverArtUrl = '';

        // retrieve spotify cover art details
        if (typeCd === 'S' && spotifyTrackId) {
            try {
                if (isSpotifyPlaylist(mediaUrl)) {
                    const playlistDetails = await getPlaylistDetails(spotifyTrackId);
                    if (playlistDetails?.coverArtUrl) {
                        coverArtUrl = playlistDetails.coverArtUrl;
                    }
                } else if (extractSpotifyId(mediaUrl).type === 'album') {
                    const albumDetails = await getAlbumDetails(spotifyTrackId);
                    if (albumDetails?.coverArtUrl) {
                        coverArtUrl = albumDetails.coverArtUrl;
                    }
                } else {
                    const trackDetails = await getTrackDetails(spotifyTrackId);
                    if (trackDetails?.coverArtUrl) {
                        coverArtUrl = trackDetails.coverArtUrl;
                    }
                }
            } catch (err) {
                console.error(err);
            }
        } else if (typeCd === 'Y' && youtubeVideoId) {
            coverArtUrl = `https://i.ytimg.com/vi_webp/${youtubeVideoId}/mqdefault.webp`;
        } else if (typeCd === 'SC' && mediaUrl) {
            try {
                // Try to fetch SoundCloud track artwork using oEmbed API
                const response = await axios.get(
                    `https://soundcloud.com/oembed?url=${encodeURIComponent(mediaUrl)}&format=json`
                );
                if (response.data.thumbnail_url) {
                    coverArtUrl = response.data.thumbnail_url;
                }
            } catch (err) {
                console.error('Error fetching SoundCloud artwork:', err);
            }
        }

        // For embedded content, use mediaUrl as the URL
        // For custom uploads, format the URL to ensure it has https:// prefix if needed
        const finalUrl = typeCd === 'C' ? formatLink(url) : mediaUrl;

        props.addHighlight(
            caption,
            finalUrl,
            uploadFile,
            mediaUrl,
            coverArtUrl,
            typeCd
        );
        resetForm();
    };

    const embedContent = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawUrl = e.target.value;

        // Skip processing if the input is empty
        if (!rawUrl.trim()) {
            setMediaUrl('');
            setErrorMsg('');
            return;
        }

        // Format the URL to ensure it has https:// prefix if needed and clean up spaces
        const contentUrl = formatLink(rawUrl);

        setMediaUrl(contentUrl);
        setErrorMsg('');

        // Try to recognize URL patterns
        const isSpotify = /spotify\.com/i.test(contentUrl);
        const isYouTube = /youtube\.com|youtu\.be/i.test(contentUrl);
        const isSoundCloud = /soundcloud\.com|on\.soundcloud\.com/i.test(contentUrl);

        if (isYouTubeVideo(contentUrl)) {
            const videoId = extractYouTubeVideoId(contentUrl);
            if (videoId) {
                setTypeCd('Y');
                setYoutubeVideoId(videoId);

                // Try to fetch the YouTube video title using the oEmbed API
                try {
                    const response = await axios.get(
                        `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`
                    );
                    // If we got a title and there's no existing caption, automatically populate it
                    if (response.data.title && !caption) {
                        setCaption(response.data.title);
                    }
                } catch (err) {
                    console.error('Error fetching YouTube title:', err);
                }

                setCurrentStep(2);
            } else {
                setErrorMsg('Invalid YouTube video or playlist link e.g. https://youtube.com/watch?v=VIDEO_ID');
            }
        } else if (isSpotifyTrack(contentUrl)) {
            const spotifyData = extractSpotifyId(contentUrl);

            if (spotifyData.id) {
                setTypeCd('S');
                setSpotifyTrackId(spotifyData.id);

                // Fetch the track or playlist details to get the title
                try {
                    if (isSpotifyPlaylist(contentUrl)) {
                        const playlistDetails = await getPlaylistDetails(spotifyData.id);
                        // If we have a title and there's no existing caption, automatically populate it
                        if (playlistDetails?.title && !caption) {
                            setCaption(playlistDetails.title);
                        }
                    } else if (spotifyData.type === 'album') {
                        const albumDetails = await getAlbumDetails(spotifyData.id);
                        if (albumDetails?.title && !caption) {
                            const artistName = albumDetails.artist || '';
                            const title = artistName
                                ? `${artistName} - ${albumDetails.title}`
                                : albumDetails.title;
                            setCaption(title);
                        }
                    } else {
                        const trackDetails = await getTrackDetails(spotifyData.id);
                        // If we have a title and there's no existing caption, automatically populate it
                        if (trackDetails?.title && !caption) {
                            const artistName = trackDetails.artist || '';
                            const title = artistName
                                ? `${artistName} - ${trackDetails.title}`
                                : trackDetails.title;
                            setCaption(title);
                        }
                    }
                } catch (err) {
                    console.error('Error fetching Spotify details:', err);
                }

                setCurrentStep(2);
            } else {
                setErrorMsg('Invalid Spotify link. Track, album, or playlist link accepted e.g. https://open.spotify.com/track/...');
            }
        } else if (isSoundCloudTrack(contentUrl)) {
            // SoundCloud embedding
            const trackUrl = extractSoundCloudTrackUrl(contentUrl);

            // Check if we need to resolve a shortened URL
            if (trackUrl.startsWith('__RESOLVE_URL__')) {
                const originalUrl = trackUrl.replace('__RESOLVE_URL__', '');

                try {
                    // Get the oEmbed data with iframe=true to get the HTML5 player
                    const oembedResponse = await axios.get(
                        `https://soundcloud.com/oembed?url=${encodeURIComponent(originalUrl)}&format=json&iframe=true`
                    );

                    if (oembedResponse.data && oembedResponse.data.html) {
                        // Extract the URL from the iframe src
                        const html = oembedResponse.data.html;
                        const urlMatch = html.match(/url=([^&]+)/);

                        if (urlMatch && urlMatch[1]) {
                            const resolvedUrl = decodeURIComponent(urlMatch[1]);

                            // Set the resolved URL for embedding
                            setTypeCd('SC');
                            setMediaUrl(resolvedUrl);

                            // Set the title if available
                            if (oembedResponse.data.title && !caption) {
                                // Extract artist and track title from the oEmbed title
                                const titleParts = oembedResponse.data.title.split(' by ');
                                if (titleParts.length === 2) {
                                    setCaption(`${titleParts[1]} - ${titleParts[0]}`);
                                } else {
                                    setCaption(oembedResponse.data.title);
                                }
                            }

                            setCurrentStep(2);
                            return;
                        }
                    }

                    // If oEmbed didn't work, try the resolve API
                    try {
                        const resolveResponse = await axios.get(
                            `https://api.soundcloud.com/resolve.json?url=${encodeURIComponent(originalUrl)}&client_id=YOUR_CLIENT_ID`
                        );

                        if (resolveResponse.data && resolveResponse.data.permalink_url) {
                            const resolvedUrl = resolveResponse.data.permalink_url;
                            setTypeCd('SC');
                            setMediaUrl(resolvedUrl);

                            if (resolveResponse.data.title && !caption) {
                                // Extract artist and track title from the resolve API response
                                const titleParts = resolveResponse.data.title.split(' by ');
                                if (titleParts.length === 2) {
                                    setCaption(`${titleParts[1]} - ${titleParts[0]}`);
                                } else {
                                    setCaption(resolveResponse.data.title);
                                }
                            }

                            setCurrentStep(2);
                            return;
                        }
                    } catch (resolveError: unknown) {
                        console.error('Error with resolve API:', resolveError);
                    }

                    // If both methods failed, show error
                    setErrorMsg('Could not resolve the SoundCloud URL. Please try the full URL format (soundcloud.com/...)');
                } catch (error: unknown) {
                    console.error('Error resolving SoundCloud URL:', error);
                    setErrorMsg('Could not resolve the SoundCloud URL. Please try the full URL format (soundcloud.com/...)');
                }
            } else if (trackUrl) {
                setTypeCd('SC');
                setMediaUrl(trackUrl);

                // Try to fetch the SoundCloud track title using the oEmbed API
                try {
                    const response = await axios.get(
                        `https://soundcloud.com/oembed?url=${encodeURIComponent(trackUrl)}&format=json`
                    );
                    // If we got a title and there's no existing caption, automatically populate it
                    if (response.data.title && !caption) {
                        // Extract artist and track title from the oEmbed title
                        const titleParts = response.data.title.split(' by ');
                        if (titleParts.length === 2) {
                            setCaption(`${titleParts[1]} - ${titleParts[0]}`);
                        } else {
                            setCaption(response.data.title);
                        }
                    }
                } catch (error: unknown) {
                    console.error('Error fetching SoundCloud title:', error);
                }

                setCurrentStep(2);
            } else {
                setErrorMsg('Invalid SoundCloud URL. Paste a track or playlist.');
            }
        } else {
            // Provide more specific error messages based on what user might be trying to input
            if (isSpotify) {
                setErrorMsg('Invalid Spotify link. Track, album, or playlist accepted e.g., https://open.spotify.com/track/...');
            } else if (isYouTube) {
                setErrorMsg('Invalid YouTube link e.g., https://youtube.com/watch?v=... or https://youtu.be/...');
            } else if (isSoundCloud) {
                setErrorMsg('Invalid SoundCloud link. Track or playlist accepted e.g., https://soundcloud.com/...');
            } else {
                setErrorMsg('Please enter a valid YouTube, Spotify, or SoundCloud link');
            }
        }
    };

    const handleCaptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCaption(e.target.value);
        // Clear the caption error if user starts typing a caption
        if (captionError && e.target.value.trim()) {
            setCaptionError('');
        }
    };

    const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setUrl(e.target.value);
        // If URL is cleared, also clear any caption error
        if (!e.target.value.trim()) {
            setCaptionError('');
        }
        // If URL is added and there's no caption, show the error
        else if (e.target.value.trim() && !caption.trim()) {
            setCaptionError('Caption is required when adding a URL');
        }
    };

    // Step 1: Content Selection
    const contentSelectionStep = (
        <div className="flex flex-col w-full h-full items-center px-6 py-4">
            <div className="relative w-full">
                <h2 className="text-xl font-bold mb-6 text-center">Add to Highlights</h2>
                <div className="absolute top-0 right-0">
                    <div className={`text-sm font-medium rounded-full px-3 py-1 ${props.highlights && props.highlights.length >= 30 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'}`}>
                        {props.highlights ? props.highlights.length : 0}/30 {props.highlights && props.highlights.length >= 30 && '⚠️'}
                    </div>
                </div>
            </div>

            {/* Image upload section */}
            <div className="w-full mb-6">
                <p className="text-sm text-gray-600 mb-2 font-medium">Upload an Image</p>
                <input
                    className="highlight-upload hidden"
                    id="highlight-upload"
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.webp"
                    onChange={(e) => onFileChange(e?.target?.files)}
                />
                <button
                    className={`flex flex-col justify-center items-center w-full h-[145px] border-2 border-dashed border-aquarius rounded-lg bg-white ${dragActive ? 'border-purple' : ''} hover:border-purple transition-colors`}
                    onClick={() =>
                        document
                            .querySelector<HTMLInputElement>('.highlight-upload')
                            ?.click()
                    }
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                >
                    <div className="flex flex-row items-center">
                        <UploadIcon width={20} height={24} />
                        <div className="flex flex-col ml-3">
                            <span className="font-sans text-lg text-black font-medium">
                                Upload image file
                            </span>
                            <span className="text-sm text-gray-500">
                                photos, event flyers, etc
                            </span>
                        </div>
                    </div>
                </button>
            </div>

            <div className="text-center w-full my-2">
                <span className="text-sm font-medium text-gray-500">OR</span>
            </div>

            {/* Embedded content section */}
            <div className="w-full mb-3">
                <p className="text-sm text-gray-600 mb-2 font-medium">Paste a link</p>
                <JWInput
                    id="content-url"
                    className="p-2 w-full border !border-solid !border-darkgray bg-white rounded-md font-normal text-darkgray"
                    placeholder={'Spotify track/album/playlist, SoundCloud, or YouTube'}
                    fullWidth={true}
                    onChange={(e) => embedContent(e)}
                    value={mediaUrl}
                />
                {errorMsg && <p className="text-red-500 text-sm mt-1">{errorMsg}</p>}
                <div className="flex items-center justify-center mt-3 space-x-3">
                    <SpotifyIcon width="35" />
                    <span className="text-gray-400">|</span>
                    <SoundcloudIconSVG width="35" height="17" />
                    <span className="text-gray-400">|</span>
                    <YoutubeIcon width="35" />
                </div>
            </div>
        </div>
    );

    // Step 2: Caption and Details
    const captionDetailsStep = (
        <div className="flex flex-col w-full h-full items-center px-6 py-3">
            <div className="w-full flex items-center mb-2 relative">
                <button
                    className="flex items-center text-gray-600 hover:text-purple-700 transition-colors"
                    onClick={goBack}
                >
                    <ChevronLeft className="h-4 w-4" />
                    Back
                </button>
                <h2 className="text-xl font-bold flex-grow text-center">Add Details</h2>
                <div className="absolute top-0 right-0">
                    <div className={`text-sm font-medium rounded-full px-3 py-1 ${props.highlights && props.highlights.length >= 30 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'}`}>
                        {props.highlights ? props.highlights.length : 0}/30 {props.highlights && props.highlights.length >= 30 && '⚠️'}
                    </div>
                </div>
            </div>

            {/* Preview Content */}
            <div className="w-full flex items-center justify-center mb-3">
                {typeCd === 'C' ? (
                    <div className="relative w-full aspect-square max-w-[400px]">
                        <img
                            className="object-cover w-full h-full rounded-md"
                            id="highlight-img-preview"
                            src=""
                            alt="Preview"
                        />
                        <button
                            className="absolute top-2 right-2 bg-white p-1 rounded-full shadow-md hover:bg-gray-100"
                            onClick={goBack}
                        >
                            <MinusCircle width={22} height={22} />
                        </button>
                    </div>
                ) : typeCd === 'Y' ? (
                    <div className="w-full aspect-square max-w-[400px] rounded-md overflow-hidden">
                        <iframe
                            width="100%"
                            height="100%"
                            src={`https://www.youtube.com/embed/${youtubeVideoId}`}
                            frameBorder="0"
                            allowFullScreen={true}
                        />
                    </div>
                ) : typeCd === 'S' ? (
                    <div className="flex items-center justify-center w-full h-full">
                        <iframe
                            src={`https://open.spotify.com/embed/${extractSpotifyId(mediaUrl).type}/${spotifyTrackId}`}
                            width="100%"
                            height="352"
                            frameBorder="0"
                            allowFullScreen={true}
                            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                            style={{ border: 'none' }}
                            className="max-w-full"
                        />
                    </div>
                ) : typeCd === 'SC' ? (
                    <div className="w-full flex items-center justify-center">
                        <div className="w-full aspect-square max-w-[400px]">
                            <iframe
                                width="100%"
                                height="100%"
                                scrolling="no"
                                frameBorder="no"
                                src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(mediaUrl)}&color=%23ff5500&auto_play=false&hide_related=false&show_comments=false&show_user=true&show_reposts=false&show_teaser=true`}
                                className="w-full h-full rounded-md"
                                allow="autoplay"
                            />
                        </div>
                    </div>
                ) : (
                    <div id="embeddedContent" className="w-full aspect-square max-w-[400px] mb-2 rounded-md overflow-hidden" />
                )}
            </div>

            {/* Caption & URL Input */}
            <div className="w-full space-y-3">
                <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Caption</label>
                    <JWInput
                        className={`p-2 w-full border !border-solid ${captionError ? '!border-red-500' : '!border-darkgray'} bg-white rounded-md font-normal text-darkgray`}
                        placeholder="Add a caption for this highlight"
                        fullWidth={true}
                        value={caption}
                        onChange={handleCaptionChange}
                    />
                    {captionError && <p className="text-red-500 text-xs mt-1">{captionError}</p>}
                </div>

                {typeCd === 'C' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">URL Link</label>
                        <JWInput
                            className="p-2 w-full border !border-solid !border-darkgray bg-white rounded-md font-normal text-darkgray"
                            placeholder="Add a URL for this highlight (optional)"
                            fullWidth={true}
                            value={url}
                            onChange={handleUrlChange}
                            onBlur={(e) => {
                                const formattedUrl = e.target.value ? formatLink(e.target.value) : '';
                                setUrl(formattedUrl);

                                // Validate after formatting
                                if (formattedUrl && !caption.trim()) {
                                    setCaptionError('Caption is required when adding a URL');
                                }
                            }}
                        />
                    </div>
                )}

                <div className="pt-2">
                    <button
                        onClick={saveHighlights}
                        disabled={props.highlights && props.highlights.length >= 30 || !!errorMsg}
                        className={`w-full bg-purple text-white rounded-md py-2 px-6 text-base font-semibold mt-4 ${(props.highlights && props.highlights.length >= 30 || !!errorMsg) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-purple-dark'}`}
                    >
                        {props.highlights && props.highlights.length >= 30 ? 'Maximum Reached' : 'Add to Highlights'}
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <JWModal
            show={props.show}
            setShow={props.setShow}
            onClose={closeModal}
            wrapperClassName={'upload-highlights-modal md:w-[480px] w-full h-auto'}
        >
            {currentStep === 1 ? contentSelectionStep : captionDetailsStep}
        </JWModal>
    );
}

export default UploadHighlightsModal;
